import { groupBy } from "lodash";
import { useEffect, useState } from "react";
import LabeledValueTable, {
  LabeledValueRow,
  LabeledValueTableProps,
} from "components/Shared/LabeledValueTable";
import DateTime from "components/Shared/DateTime";
import {
  DocumentType,
  FragmentType,
  getFragmentData,
  gql,
} from "__generated__/gql";
import { EmailMessageParticipantType } from "__generated__/gql/graphql";
import Participants, { PARTICIPANT_TYPE_LABELS } from "./Participants";

const fragment = gql(/* GraphQL */ `
  fragment SelectedEmailPreviewHeader on ReceivedEmailMessage {
    emailMessageId
    nonTechnicalReceiversList(
      orderBy: [ORDER_ASC, DISPLAY_NAME_ASC, EMAIL_ADDRESS__EMAIL_ASC]
    ) {
      participantType
      ...EmailMessageParticipants
    }
    emailMessage {
      id
      subject
      sentAt
      originalFrom {
        ...EmailMessageParticipants
      }
      emailMessageParticipantsList(
        filter: { participantType: { notEqualTo: FROM } }
        orderBy: [ORDER_ASC, DISPLAY_NAME_ASC, EMAIL_ADDRESS__EMAIL_ASC]
      ) {
        participantType
        ...EmailMessageParticipants
      }
    }
  }
`);

type EmailMessageParticipant = NonNullable<
  DocumentType<typeof fragment>["emailMessage"]
>["emailMessageParticipantsList"][number];

type Props = LabeledValueTableProps & {
  data: FragmentType<typeof fragment> | null | undefined;
  isLoading?: boolean;
  isReceiverShowAllToggleEnabled?: boolean;
  isSubjectHidden?: boolean;
  participantTypes?: EmailMessageParticipantType[];
};

export default function PreviewHeader({
  data,
  isLoading,
  isReceiverShowAllToggleEnabled = false,
  isSubjectHidden,
  participantTypes = [
    EmailMessageParticipantType.TO,
    EmailMessageParticipantType.CC,
    EmailMessageParticipantType.BCC,
    EmailMessageParticipantType.RECEIVER,
  ],
  ...rest
}: Props) {
  const rem = getFragmentData(fragment, data);
  const [isShowingAllReceivers, setIsShowingAllReceivers] = useState(false);

  useEffect(() => {
    setIsShowingAllReceivers(false);
  }, [rem?.emailMessageId]);

  const participantsByType = groupBy(
    rem?.emailMessage?.emailMessageParticipantsList,
    "participantType",
  ) as Partial<Record<EmailMessageParticipantType, EmailMessageParticipant[]>>;
  const allReceiverParticipants =
    participantsByType[EmailMessageParticipantType.RECEIVER];
  const receiverParticipants = isShowingAllReceivers
    ? allReceiverParticipants
    : rem?.nonTechnicalReceiversList;

  return (
    <LabeledValueTable
      width="100%"
      sx={{ tableLayout: "fixed" }}
      labelProps={{ width: "8.5em" }}
      valueProps={{ isTruncated: true }}
      cellProps={{ py: ".4em" }}
      isLoading={isLoading}
      {...rest}
    >
      {!isSubjectHidden && (
        <LabeledValueRow
          label="Subject:"
          value={rem?.emailMessage?.subject}
          valueProps={{ fontWeight: 500 }}
        />
      )}

      <LabeledValueRow
        label="Received at:"
        value={
          rem?.emailMessage?.sentAt && (
            <DateTime datetime={rem.emailMessage?.sentAt} format="PPPpp" />
          )
        }
      />

      <LabeledValueRow
        label={`${PARTICIPANT_TYPE_LABELS[EmailMessageParticipantType.FROM]}:`}
        value={
          rem?.emailMessage?.originalFrom && (
            <Participants
              isEmailShown
              participants={[rem.emailMessage.originalFrom]}
            />
          )
        }
      />

      {participantTypes
        .filter(
          (participantType) =>
            participantType !== EmailMessageParticipantType.RECEIVER,
        )
        .map((participantType) => {
          const participants = participantsByType[participantType];

          if (
            !participants?.length &&
            participantType !== EmailMessageParticipantType.TO
          ) {
            return null;
          }

          return (
            <LabeledValueRow
              key={participantType}
              label={`${PARTICIPANT_TYPE_LABELS[participantType]}:`}
              value={<Participants isEmailShown participants={participants} />}
              isValueLoaded={!!participants}
              isValuePresent={!!participants?.length}
            />
          );
        })}

      {participantTypes.includes(EmailMessageParticipantType.RECEIVER) &&
        (receiverParticipants?.length || isReceiverShowAllToggleEnabled) && (
          <LabeledValueRow
            label={`${
              PARTICIPANT_TYPE_LABELS[EmailMessageParticipantType.RECEIVER]
            }:`}
            value={
              <Participants
                isEmailShown
                participants={receiverParticipants}
                moreLabel="Show all"
                numTotal={
                  isReceiverShowAllToggleEnabled
                    ? allReceiverParticipants?.length
                    : undefined
                }
                onMoreClick={(event) => {
                  event.stopPropagation();
                  setIsShowingAllReceivers(true);
                }}
              />
            }
            isValueLoaded={!!allReceiverParticipants}
            isValuePresent={!!allReceiverParticipants?.length}
          />
        )}
    </LabeledValueTable>
  );
}
